import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/authContext';
import { CircularProgress } from '@/components/ui/circular-progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertTriangle, Lock } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string | string[];
  requiredPermission?: { resource: string; action: string };
  fallbackPath?: string;
  requireAll?: boolean; // For multiple roles, require all instead of any
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallbackPath = '/login',
  requireAll = false
}) => {
  const { isAuthenticated, user, isLoading, hasPermission, hasRole } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <CircularProgress className="w-12 h-12 text-primary" />
          <div>
            <p className="text-lg font-medium text-foreground">Loading...</p>
            <p className="text-sm text-muted-foreground">Verifying your credentials</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to={fallbackPath}
        state={{ from: location, requiredRole, requiredPermission }}
        replace
      />
    );
  }

  // Check if user account is active
  if (user?.status !== 'active') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full">
          <Alert className="border-warning/50 bg-warning/5">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <AlertDescription className="text-warning">
              <div className="space-y-2">
                <p className="font-medium">Account Not Active</p>
                <p className="text-sm">
                  Your account is currently {user?.status}. Please contact support or check your email for activation instructions.
                </p>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Check role-based access
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

    const hasRequiredRole = requireAll
      ? roles.every(role => hasRole(role))
      : roles.some(role => hasRole(role));

    if (!hasRequiredRole) {
      const roleNames = roles.join(' or ');

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto">
                <Lock className="w-8 h-8 text-warning" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
                <p className="text-muted-foreground mt-2">
                  You don't have the required role to access this page.
                </p>
              </div>
            </div>

            <Alert className="border-warning/50 bg-warning/5">
              <Shield className="h-4 w-4 text-warning" />
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-medium">Required Role:</p>
                  <p className="text-sm">{roleNames}</p>
                  <p className="text-sm">Your current role: { roles.join(', ')}</p>
                </div>
              </AlertDescription>
            </Alert>

            <div className="flex flex-col space-y-2">
              <button
                onClick={() => window.history.back()}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
              >
                Go Back
              </button>
              <button
                onClick={() => window.location.href = '/admin/dashboard'}
                className="w-full px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // Check permission-based access
  if (requiredPermission && !hasPermission(requiredPermission.resource, requiredPermission.action)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
              <Lock className="w-8 h-8 text-destructive" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Permission Denied</h1>
              <p className="text-muted-foreground mt-2">
                You don't have the required permission to access this resource.
              </p>
            </div>
          </div>

          <Alert className="border-destructive/50 bg-destructive/5">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Required Permission:</p>
                <p className="text-sm">{requiredPermission.action} {requiredPermission.resource}</p>
                <p className="text-sm">Your role: {user?.role?.replace('_', ' ')?.replace(/\b\w/g, l => l.toUpperCase())}</p>
              </div>
            </AlertDescription>
          </Alert>

          <div className="flex flex-col space-y-2">
            <button
              onClick={() => window.history.back()}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/admin/dashboard'}
              className="w-full px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
};

export default ProtectedRoute;